import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useRegisterSW } from 'virtual:pwa-register/react';

export function UpdatePrompt() {
  const [showPrompt, setShowPrompt] = useState(false);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl, registration) {
      console.log('SW registered:', swUrl);
      
      if (registration) {
        // Check for updates more frequently on iOS (every 5 minutes)
        const isIOS = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
        const interval = isIOS ? 5 * 60 * 1000 : 60 * 60 * 1000;
        
        setInterval(() => {
          registration.update();
        }, interval);

        // Also check on visibility change (when user returns to app)
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            registration.update();
          }
        });
      }
    },
    onRegisterError(error) {
      console.error('SW registration error:', error);
    },
  });

  useEffect(() => {
    if (needRefresh) {
      setShowPrompt(true);
    }
  }, [needRefresh]);

  // On iOS, also add a manual check mechanism
  useEffect(() => {
    const isIOS = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
    const isStandalone = (window.navigator as any).standalone === true;
    
    if (isIOS && isStandalone) {
      // Check for app version on load
      const lastVersion = localStorage.getItem('vcm-app-version');
      const currentVersion = import.meta.env.VITE_APP_VERSION || Date.now().toString();
      
      if (lastVersion && lastVersion !== currentVersion) {
        // Version changed, prompt for refresh
        setShowPrompt(true);
      }
      
      localStorage.setItem('vcm-app-version', currentVersion);
    }
  }, []);

  const handleUpdate = async () => {
    // Clear all caches before updating
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }
    
    await updateServiceWorker(true);
    setShowPrompt(false);
    
    // Force reload on iOS
    window.location.reload();
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setNeedRefresh(false);
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed z-[60] animate-fade-in-up left-4 right-4 md:left-auto md:right-4 md:max-w-sm" style={{ top: 'max(1rem, env(safe-area-inset-top))' }}>
      <div className="rounded-xl border border-primary/20 bg-card p-4 shadow-lg backdrop-blur-sm ml-[env(safe-area-inset-left)] mr-[env(safe-area-inset-right)]">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <RefreshCw className="h-5 w-5 text-primary" />
          </div>

          <div className="flex-1">
            <h3 className="font-semibold text-foreground">Update Available</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              A new version of VCM is ready. Tap update to get the latest features.
            </p>
          </div>
        </div>

        <div className="mt-4 flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="flex-1"
          >
            Later
          </Button>
          <Button
            size="sm"
            onClick={handleUpdate}
            className="flex-1 bg-gradient-to-r from-primary to-accent text-primary-foreground hover:opacity-90"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Update Now
          </Button>
        </div>
      </div>
    </div>
  );
}
